/**
 * 质检域工具（规则类型标签 / 分数→色 / 实际扣分）：
 * 违规点复核结论（human_status）的文案/颜色单源已收口至
 * utils/labels.ts STATUS_LABELS（statusLabel）与 components/StatusTag.tsx COLOR_MAP，
 * 渲染统一走 <StatusTag value={human_status}/>（见 Reports/FlowReports），本文件不再重复定义。
 */
import { scoreColor } from '../theme/palette';

/** 质检规则类型中文（keyword/forbidden/silence/llm + 实时 overlap + consistency/answer + 流程类） */
export const RULE_TYPE_LABELS: Record<string, string> = {
  keyword: '关键词',
  forbidden: '违禁语',
  silence: '静默时长',
  overlap: '实时抢话',
  llm: 'LLM 评分',
  consistency: '业务一致性',
  answer: '答案比对',
  emotion: '情绪趋势',
  process_dwell: '流程驻留',
  process_coverage: '流程覆盖',
  process_keyword: '流程关键词',
  process_llm: '流程 LLM',
};

export function ruleTypeLabel(v?: string): string {
  return v ? RULE_TYPE_LABELS[v] || v : '-';
}

/** 质检报告分数 → 状态色（≥80 优 / ≥60 中 / <60 差，全模块统一口径） */
export { scoreColor };

/** 质检报告分数 → Arco Tag color */
export function scoreTagColor(score: number | undefined | null): string {
  const s = Number(score);
  if (Number.isNaN(s) || s <= 0) return 'gray';
  if (s >= 80) return 'green';
  if (s >= 60) return 'orange';
  return 'red';
}

/** 扣分档步长（百分制满分 100 ÷ 5 档）= 20，与后端 qualityscore.DeductStep 同源。 */
export const DEDUCT_STEP = 20;

/** 规则得分（0–5）→ 百分制扣分（0/20/40/60/80/100）。 */
export function deductFromAwarded(awarded: number): number {
  const a = Math.min(5, Math.max(0, Math.round(Number(awarded) || 0)));
  return (5 - a) * DEDUCT_STEP;
}

/** 百分制扣分（0–100）→ 规则得分（0–5），按最近的 20 分档取整。 */
export function awardedFromDeduct(deduct: number): number {
  const d = Math.min(100, Math.max(0, Number(deduct) || 0));
  return Math.min(5, Math.max(0, 5 - Math.round(d / DEDUCT_STEP)));
}

/** 人工改分输入合法性：0–100 且为 20 的倍数（与后端 qualityscore.IsValidDeduct 同口径）。 */
export function isValidDeduct(deduct?: number | null): boolean {
  const d = Number(deduct);
  return Number.isInteger(d) && d >= 0 && d <= 100 && d % DEDUCT_STEP === 0;
}

/**
 * 违规点实际生效扣分（返回正数扣分值，供「-N 分」展示）：
 * 人工改分（CORRECTED）的 corrected_score 存的是 0–5 得分，换算成百分制扣分展示（不再是「新得分」直接当扣分）；
 * 误判（MISJUDGED）不计分返回 0；其余取原始 deduct。
 */
export function effectiveDeduct(v?: { deduct?: number; human_status?: string; corrected_score?: number | null } | null): number {
  if (!v) return 0;
  if (v.human_status === 'MISJUDGED') return 0;
  if (v.human_status === 'CORRECTED' && v.corrected_score != null) return deductFromAwarded(Number(v.corrected_score) || 0);
  return Math.max(0, Number(v.deduct) || 0);
}

/** 情绪标签 → Arco Tag color（正面绿/中性灰/焦虑橙/愤怒红，全模块统一） */
export function emotionTagColor(e?: string): string {
  if (e === '愤怒') return 'red';
  if (e === '焦虑') return 'orange';
  if (e === '正面') return 'green';
  return 'gray';
}

/** 情绪类别权重（正面<中性<焦虑<愤怒） */
const EMOTION_RANK: Record<string, number> = { 正面: 0, 中性: 1, 焦虑: 2, 愤怒: 3 };
/** 情绪强度权重（低<中<高）：同一类别下强度变化同样算趋势，否则「愤怒(低)→愤怒(高)」会被误判为平稳 */
const EMOTION_LEVEL_RANK: Record<string, number> = { 低: 0, 中: 1, 高: 2 };

/** 情绪采样点排序值（类别权重 3 + 强度权重）；未知情绪按中性、未知强度按中计 */
function emotionRank(r: { emotion?: string; level?: string }): number {
  return (EMOTION_RANK[r.emotion || ''] ?? 1) * 3 + (EMOTION_LEVEL_RANK[r.level || ''] ?? 1);
}

/** 情绪趋势结论（单源口径） */
export type EmotionTrend = '升级' | '好转' | '平稳' | '样本不足' | '无趋势';

/**
 * 情绪趋势：最近 3 个采样点的方向（类别 + 强度合成排序）。
 * 采样 <2 点无趋势、2 点不出方向结论（样本不足）——避免两点相同时误报「平稳」。
 */
export function emotionTrend(rows: { emotion?: string; level?: string }[]): EmotionTrend {
  const last3 = rows.slice(-3);
  if (last3.length < 2) return '无趋势';
  if (last3.length < 3) return '样本不足';
  const delta = emotionRank(last3[2]) - emotionRank(last3[0]);
  if (delta > 0) return '升级';
  if (delta < 0) return '好转';
  return '平稳';
}

/** 情绪趋势 → 语义色（升级红 / 好转绿 / 平稳·样本不足灰） */
export function emotionTrendColor(t: EmotionTrend): string {
  if (t === '升级') return 'red';
  if (t === '好转') return 'green';
  return 'gray';
}

/** 情绪趋势（报告侧文案口径，供序列视图统一展示） */
export function emotionTrendText(rows: { emotion?: string; level?: string }[]): string {
  const t = emotionTrend(rows);
  if (t === '无趋势') return '无趋势';
  if (t === '样本不足') return '趋势样本不足（≥3 点）';
  if (t === '升级') return '升级（平静→不满→愤怒）';
  return t;
}

/** 实时告警类型（rule=规则命中 / emotion=情绪预警 / trend=情绪升级趋势） */
export const ALERT_TYPE_LABELS: Record<string, string> = {
  rule: '规则命中',
  emotion: '情绪预警',
  trend: '情绪升级趋势',
  // 服务结束后按 0–5 分评分产出的「单规则低分」告警（得分 ≤2 触发）
  rule_low_score: '规则低分',
};

export function alertTypeLabel(v?: string): string {
  return v ? ALERT_TYPE_LABELS[v] || v : '规则命中';
}

/** 坐席打标 / 质检员裁定结论 → 文案与颜色（none=未打标） */
export const ALERT_VERDICT_LABELS: Record<string, string> = {
  tp: '准确',
  fp: '误报',
  handled: '已处理',
};

export function alertVerdictLabel(v?: string): string {
  if (!v || v === 'none') return '';
  return ALERT_VERDICT_LABELS[v] || v;
}

export function alertVerdictColor(v?: string): string {
  if (v === 'fp') return 'red';
  if (v === 'tp') return 'green';
  if (v === 'handled') return 'arcoblue';
  return 'gray';
}

/** 六档评分标准：档位（"0"~"5"）→ 该档得分机制描述 */
export type ScoreStandard = Record<string, string>;

/** 六个档位（0–5 分），表单与只读展示共用同一顺序 */
export const SCORE_TIERS = [0, 1, 2, 3, 4, 5] as const;

/**
 * 解析规则返回的 score_standard：接口可能给 JSON 字符串，也可能已被 axios 解析成对象
 * （后端 JSON 列为空时还会给空串），故统一收敛为「一定是对象」。
 */
export function parseScoreStandard(raw: unknown): ScoreStandard {
  if (!raw) return {};
  if (typeof raw === 'object') return raw as ScoreStandard;
  try {
    const v = JSON.parse(String(raw));
    return typeof v === 'object' && v ? (v as ScoreStandard) : {};
  } catch {
    return {};
  }
}

/** 评分标准摘要（列表/预览的只读单行展示）：只列已填档位，全空给「未填写评分标准」 */
export function scoreStandardSummary(raw: unknown): string {
  const m = parseScoreStandard(raw);
  const parts = SCORE_TIERS.filter((t) => String(m[String(t)] || '').trim()).map(
    (t) => `${t}分 ${String(m[String(t)]).trim()}`,
  );
  return parts.length ? parts.join('；') : '未填写评分标准';
}
